import { useContext, useEffect } from 'react';
import router from 'next/router';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { ServerSideErrorModal, LoadingView } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { getVariantsSortedByPrice, truncatePrice } from '../../products';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { OnboardingIntro } from '../OnboardingIntro';
import { PupNameForm } from '../PupNameForm';
import { PupGenderForm } from '../PupGenderForm';
import { PupStatusForm } from '../PupStatusForm';
import { UserInfoForm } from '../UserInfoForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import { RecommendedMealPlanForm } from '../RecommendedPlanForm';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import recommendedPlanPhoto from '../../images/onboarding/onboarding-v2/ollie-pork-bowl.png';
import { QuizHeader } from '../QuizHeader';
import { HighlightText } from '../RecommendedPlanFormOptimized/styles';
import { RecommendedPlanFormOptimized } from '../RecommendedPlanFormOptimized';
import { AllergyForm } from '../AllergyForm';
import { AllergySelectionForm } from '../AllergySelectionForm';
import { useAllergies } from '../../allergies';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { calculateDiscountedPrice } from '../../offer/helpers';
import { AddOnSelectionForm } from '../AddOnSelectionForm';
import {
  recipes as freshRecipes,
  dryRecipes,
  mealPlanModalContent,
  MEAL_PREP_TYPES,
} from '../constants';
import { OnboardingContext } from './context';
import { interpolations } from './interpolation';
import {
  onCartSubmission,
  getLowestCostVariant,
  getRecipeCombination,
  getUnavailableRecipes,
  getSelectedAllergies,
} from './functions';
import {
  STATE,
  ACTIONS,
  BC_TO_SHOPIFY_SKU_MAP,
  levelToLabel,
  MEAL_PREP_CRO_DATA,
  RECOMMENDED_PLAN_CRO_DATA,
} from './constants';
import { Main, Wrapper } from './styles';
import {
  userSkipsExtraSelection,
  userContinuesWithExtraSelection,
} from './events';

const QuizContainer = ({
  stepNumber,
  children,
  steps = 7,
  showPromoBar = false,
  progress = true,
  overrideText = '',
}) => {
  const { showModal, setShowModal } = useContext(OnboardingContext);
  return (
    <Wrapper>
      <QuizHeader
        showPromoBar={showPromoBar}
        progress={progress}
        overrideText={overrideText}
        currentStep={stepNumber}
        totalSteps={steps}
      />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  steps: PropTypes.number,
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
  showPromoBar: PropTypes.bool,
  progress: PropTypes.bool,
  overrideText: PropTypes.string,
};

const IntroPage = props => {
  const offer = useShopifyOfferContext();
  const { send } = useContext(OnboardingContext);
  const goBack = () => router.push('/');

  return (
    <OnboardingIntro
      {...props}
      goBack={goBack}
      onContinue={() => send(ACTIONS.toPupName())}
      popUpText={interpolate(props.popUpText, { offer: offer.formattedAmount })}
    />
  );
};

const PupName = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);
  const defaultValues = {
    name: pet?.name,
  };

  const onSubmit = async ({ name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupGender({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  return (
    <QuizContainer stepNumber={1}>
      <PupNameForm
        index={1}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...props}
      />
    </QuizContainer>
  );
};

const PupGender = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);

  const defaultValues = {
    gender: pet?.gender,
  };
  const genderList = [
    { name: 'Female', value: 'Female' },
    { name: 'Male', value: 'Male' },
  ];
  const onSubmit = async ({ gender }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupStatus({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 2,
  };

  return (
    <QuizContainer stepNumber={2}>
      <PupGenderForm
        genderList={genderList}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupStatus = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);

  const defaultValues = {
    spayedStatus: pet?.is_neutered?.toString(),
  };

  const onSubmit = async ({ spayedStatus }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ is_neutered: spayedStatus });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toWeight({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 3,
  };

  return (
    <QuizContainer stepNumber={3}>
      <PupStatusForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Weight = props => {
  const { pet, savePet, send, goBack, goToLogin, reporter, setShowModal } =
    useContext(OnboardingContext);
  const defaultValues = {
    weight: pet?.weight,
    idealWeight: pet?.ideal_weight,
  };

  const onSubmit = async ({ weight, idealWeight }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
      });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 4,
  };

  const isPuppy = false;

  return (
    <QuizContainer stepNumber={4}>
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const { pet, savePet, send, goBack, goToLogin, reporter, setShowModal } =
    useContext(OnboardingContext);
  const defaultValues = {
    activity: pet?.activity_level,
  };
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toAllergies({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 5,
  };
  return (
    <QuizContainer stepNumber={5}>
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const { pet, cart, send, goBack, savePet, reporter, customer, setShowModal } =
    useContext(OnboardingContext);
  const defaultValues = {
    allergy:
      pet?.allergies.length > 0 ? 'true' : pet.starter_box ? 'false' : null,
  };

  const onSubmit = async allergy => {
    let result, payload;
    if (allergy.allergy === 'true') {
      send(ACTIONS.toAllergySelection({ pet }));
    } else {
      try {
        //to do: ensure this is accepted after BE changes
        // Wait for meal plans to refetch since clearing allergies affects available recipes
        const response = await savePet(
          { allergy_ids: undefined },
          { waitForMealPlans: true },
        );

        payload = {
          action: 'update',
          pet: response.data.pet,
          pets: response.data.pets,
        };
        result = ApiResult.success(payload);
      } catch (err) {
        result = wrapErrorWithResult(err);
      }

      ApiResult.match(result, {
        success: async payload => {
          if (customer) {
            send(
              ACTIONS.toRecommendedPlan({
                customer,
                pet: payload.pet,
                pets: payload.pets,
                cart,
                ...payload,
              }),
            );
          }

          send(
            ACTIONS.toUserInfo({
              pet: payload.pet,
              pets: payload.pets,
              cart,
              allergies: [],
              ...payload,
            }),
          );
        },
        error: {
          client: error => reporter.error(error.original),
          server: () => setShowModal(true),
        },
      });
      return result;
    }
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 6,
  };
  return (
    <QuizContainer stepNumber={6}>
      <AllergyForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const AllergySelection = props => {
  const {
    pet,
    savePet,
    isLocalPet,
    send,
    cart,
    goBack,
    goToLogin,
    reporter,
    customer,
    setShowModal,
    setShowPlanSelection,
  } = useContext(OnboardingContext);
  const allAllergies = useAllergies();
  const normalize = allergies =>
    allergies?.map(allergy => ({
      label: allergy.name,
      value: allergy.id,
    }));
  const selectedAllergies =
    pet?.allergies.length > 0
      ? normalize(getSelectedAllergies(pet?.allergies, allAllergies))
      : [];

  const defaultValues = {
    allergies: selectedAllergies,
  };

  const onSubmit = async ({ allergies }) => {
    const allergyIds = allergies?.map(({ value, label }) =>
      isLocalPet ? { id: value, name: label } : value,
    );
    let result, payload;
    try {
      // Wait for meal plans to refetch since allergies affect available recipes
      const response = await savePet(
        {
          allergy_ids: allergyIds.length === 0 ? undefined : allergyIds,
        },
        { waitForMealPlans: true },
      );

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
        allergies: allergies.map(({ value, label }) => label),
        allergiesIds: allergyIds,
      };

      result = ApiResult.success(payload);
      setShowPlanSelection(false);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (customer) {
          send(
            ACTIONS.toRecommendedPlan({
              customer,
              pet: payload.pet,
              pets: payload.pets,
              cart,
              ...payload,
            }),
          );
        }

        send(
          ACTIONS.toUserInfo({
            pet: payload.pet,
            ...payload,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 7,
  };
  return (
    <QuizContainer stepNumber={6}>
      <AllergySelectionForm
        placeholderAllergiesCount={9}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const UserInfo = props => {
  const {
    customer,
    pet,
    send,
    attachCustomer,
    goToLogin,
    reporter,
    setShowModal,
    goBack,
    setShowPlanSelection,
  } = useContext(OnboardingContext);

  const defaultValues = {
    firstName: customer?.first_name || '',
    email: customer?.email || '',
  };

  const onSubmit = async ({ firstName, email }) => {
    let result;

    try {
      //* Payload returns the entire cart object which includes the customer and pets
      let payload = await attachCustomer({
        ...(firstName ? { first_name: firstName } : {}),
        last_name: 'Fake-LastName',
        email,
      });

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    setShowPlanSelection(false);

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toRecommendedPlan({ ...payload }));
      },
      error: {
        //* 404 Error (Session ID not found)
        //* 400 Error (Invalid payload)
        //* 422 Error (Internal error)
        client: error => reporter.error(error.original),
        //* 401 Error (Existing customer)
        existingCustomer: () => goToLogin(),
        //* 500 error (All other errors)
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 0,
  };

  return (
    <QuizContainer
      progress={false}
      stepNumber={7}
      overrideText={'Save Your Info'}
    >
      <UserInfoForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        petName={pet?.name}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const getUnavailableMealPrepTypes = combinationsByPlanType => {
  return MEAL_PREP_TYPES.filter(type => {
    const planType = combinationsByPlanType[type];
    return (
      !planType ||
      !planType.planCombinations ||
      planType.planCombinations.length === 0
    );
  });
};

const RecommendedPlan = props => {
  const {
    setShowModal,
    reporter,
    combinationsByPlanType,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
    setLocalSelection,
    mealPlanForPet,
    flags: { onboardingFlowUI, hideMixed },
    campaignId,
  } = useContext(OnboardingContext);
  const { type, amount, formattedAmount } = useShopifyOfferContext();
  let combinationId = null;
  const defaultPlanType = mealPlanForPet
    ? mealPlanForPet.plan_type_name
    : 'fresh';
  const defaultFreshRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selections.fresh
    : combinationsByPlanType.fresh?.recommendedCombination?.recipes?.fresh ??
      [];
  const defaultBakedRecipes =
    mealPlanForPet?.plan_type_name === 'mixed'
      ? mealPlanForPet.recipe_selections.baked
      : combinationsByPlanType.mixed?.recommendedCombination?.recipes?.baked ??
        [];

  const unavailableFreshRecipes = getUnavailableRecipes(
    combinationsByPlanType.fresh?.availableRecipes?.fresh || [],
  );
  const unavailableBakedRecipes = getUnavailableRecipes(
    combinationsByPlanType.baked?.availableRecipes?.baked || [],
  );

  const unavailableMealPrepTypes = getUnavailableMealPrepTypes(
    combinationsByPlanType,
  );

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      const product = combinationsByPlanType[selectedMealPrepType];

      if (!selectedFreshRecipes.length)
        return { price: null, message: 'Choose one or more recipes.' };

      const variants =
        product && Array.isArray(product.planCombinations)
          ? getVariantsSortedByPrice(product.planCombinations)
          : [];
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: [],
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
      };
    } else {
      const product = combinationsByPlanType['mixed'];

      if (!selectedFreshRecipes.length)
        return {
          price: null,
          message: 'Choose one or more fresh recipes.',
        };

      if (!selectedBakedRecipes.length)
        return {
          price: null,
          message: 'Choose one or more baked recipes.',
        };

      const variants = getVariantsSortedByPrice(product.planCombinations);
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: selectedBakedRecipes,
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
      };
    }
  };
  const lowestMixedVariant = combinationsByPlanType?.mixed?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.mixed.planCombinations)
    : null;
  let lowestFreshVariant = combinationsByPlanType?.fresh?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.fresh.planCombinations)
    : null;
  const lowestHalfFreshVariant = combinationsByPlanType?.half_fresh
    ?.planCombinations?.length
    ? getLowestCostVariant(combinationsByPlanType.half_fresh.planCombinations)
    : null;
  const lowestBakedVariant = combinationsByPlanType?.dry?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.dry.planCombinations)
    : null;

  const productTypeInterpolationSubstitutes = {
    freshPricePerWeek: lowestFreshVariant?.price_per_week,
    freshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestFreshVariant?.price_per_week,
      type,
      amount,
    ),
    mixedPricePerWeek: lowestMixedVariant?.price_per_week,
    mixedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestMixedVariant?.price_per_week,
      type,
      amount,
    ),
    halfFreshPricePerWeek: lowestHalfFreshVariant?.price_per_week,
    halfFreshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestHalfFreshVariant?.price_per_week,
      type,
      amount,
    ),
    bakedPricePerWeek: lowestBakedVariant?.price_per_week,
    bakedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestBakedVariant?.price_per_week,
      type,
      amount,
    ),
  };

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          {
            recipes: {
              dry: bakedRecipes,
              fresh: freshRecipes,
              planType: planType,
            },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          { recipes: freshRecipes, planType: planType },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };

  const mealPrepData = hideMixed
    ? interpolate(
        MEAL_PREP_CRO_DATA.planSelectionInfo.items,
        productTypeInterpolationSubstitutes,
      )
    : interpolate(
        MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.items,
        productTypeInterpolationSubstitutes,
      );

  const pupInfo = [
    {
      body: (
        <p>
          Recipes for {pet?.name}'s{' '}
          <HighlightText>{levelToLabel[pet.activity_level]}</HighlightText>{' '}
          lifestyle
        </p>
      ),
    },
    {
      body: (
        <p>
          Calories and nutrition for {interpolations(pet)?.pupPossessivePronoun}{' '}
          <HighlightText>{pet?.weight}lb weight</HighlightText>
        </p>
      ),
    },
  ];
  if (pet.allergies && pet.allergies.length > 0) {
    pupInfo.push({
      body: (
        <p>
          Ensured no{' '}
          <HighlightText>
            {pet.allergies.map(a => a.name).join(', ')}
          </HighlightText>{' '}
          was in recipes
        </p>
      ),
    });
  }

  //TODO: Remove MEAL_PREP_CRO_DATA when we have fully completed the redesign
  const mealplanProps = {
    onSubmit: onMealPlanAndRecipesSubmit,
    headline: `Recommended for ${pet.name}`,
    subheadline: `As a ${pet.weight}-pound ${
      levelToLabel[pet.activity_level]
    } pup, we've created meals perfect for ${
      pet.name
    }'s complete nutrition needs.`,
    ctaText: `Continue with ${formattedAmount} off`,
    modals: [...freshModalsContent, ...dryModalsContent],
    freshRecipes: freshRecipes,
    defaultFreshRecipes: defaultFreshRecipes ?? [],
    bakedRecipes: dryRecipes,
    defaultBakedRecipes: defaultBakedRecipes ?? [],
    defaultMealPrepType: defaultPlanType,
    mealPrepTypes: mealPrepData,
    collapsedMealPrepTypes: interpolate(
      MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.collapsedItems,
      productTypeInterpolationSubstitutes,
    ),
    unavailableMealPrepTypes: unavailableMealPrepTypes,
    unavailableFreshRecipes: unavailableFreshRecipes,
    unavailableBakedRecipes: unavailableBakedRecipes,
    onBackButtonClick: goBack,
    period: pricePeriod,
    getConfigFromRecipes: getConfigFromRecipes,
    splitScreenPromoProps: RECOMMENDED_PLAN_CRO_DATA,
    headerImage: planHeaderPhoto,
    modalContent: interpolate(
      mealPlanModalContent,
      productTypeInterpolationSubstitutes,
    ),
    notSelectableText: `Based on your answers, this meal plan is unavailable for ${pet.name}.`,
    ctaButtonVariant: 'sea2',
    hideMixed: hideMixed,
  };

  return onboardingFlowUI === 'cro_optimized_recommended' ? (
    <>
      <RecommendedPlanFormOptimized
        onRecommendedSubmit={onMealPlanAndRecipesSubmit}
        headline={`${pet.name}'s full fresh recommended plan`}
        onBackButtonClick={goBack}
        reviewsCopy={' 20,000+ 5-star reviews'}
        savePercentText={`${formattedAmount} off`}
        defaultRecipes={defaultFreshRecipes}
        getConfigFromRecipes={getConfigFromRecipes}
        planImage={recommendedPlanPhoto}
        pupInfo={pupInfo}
        ctaButtonText={'Continue'}
        mealPlanProps={mealplanProps}
        period={pricePeriod}
      />
    </>
  ) : (
    <QuizContainer progress={false} overrideText={'Your plan'}>
      <RecommendedMealPlanForm {...mealplanProps} />
    </QuizContainer>
  );
};

RecommendedPlan.displayName = 'RecommendedPlan';
RecommendedPlan.propTypes = {};

const Extras = props => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    attachExtras,
    send,
    pets,
    selectedAddons: defaultAddons,
    treatProducts,
    customer,
    supplementProducts,
    treatsForLife,
  } = useContext(OnboardingContext);
  const { formattedAmount, type, amount } = useShopifyOfferContext();

  const findProductBySku = (products, sku) => {
    const product = products.find(product => product.sku === sku);
    return product;
  };

  const mergedAddonSection = props.addonSections.map((section, i) => {
    const addons = section.addons
      .map(addon => {
        const productType = addon.productType;
        const products =
          productType === 'TR' || productType === 'DC'
            ? treatProducts
            : supplementProducts;

        const mappedSku = BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku;
        const product = findProductBySku(products, mappedSku);

        if (!product) {
          return null;
        }

        const discountedPrice = calculateDiscountedPrice(
          product.price,
          type,
          amount,
        );

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(discountedPrice),
          petName: pet.name,
          addonSavePercentage: formattedAmount,
        });
      })
      .filter(Boolean);

    return {
      ...section,
      addons,
    };
  });

  const mergedModals = props.modals
    .map(modal => {
      const product = findProductBySku(
        [...supplementProducts, ...treatProducts],
        BC_TO_SHOPIFY_SKU_MAP[modal.sku] || modal.sku,
      );
      if (!product) return null;

      const discountedPrice = calculateDiscountedPrice(
        product.price,
        type,
        amount,
      );

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(discountedPrice),
        addonSavePercentage: formattedAmount,
      });
    })
    .filter(Boolean);

  const onSkip = event => {
    event.preventDefault();
    reporter.tag(userSkipsExtraSelection());
    if (customer) {
      send(ACTIONS.toCheckout({ pet, pets }));
    } else {
      send(ACTIONS.toUserInfo({ pet, pets }));
    }
  };

  const onSubmit = async selectedAddons => {
    let result = ApiResult.success();
    const attachResult = await attachExtras(
      petId,
      selectedAddons.map(addon => ({
        sku: BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku,
        frequency: addon.frequency,
        quantity: addon.quantity,
        recurrence: addon.recurrence,
      })),
    );

    result = ApiResult.success(attachResult)
      ? attachResult
      : ApiResult.error.client('Failed to attach extras', attachResult.error);

    ApiResult.match(result, {
      success: () => {
        reporter.tag(userContinuesWithExtraSelection(selectedAddons));
        if (customer) {
          send(ACTIONS.toCheckout({ pet: pet, pets, extras: selectedAddons }));
        } else {
          send(ACTIONS.toUserInfo({ pet, extras: selectedAddons }));
        }
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const headline = interpolate(props.headline, { pupName: pet.name });
  const unavailableAddons = [];

  return (
    <QuizContainer overrideText={'Your plan'} progress={false}>
      <AddOnSelectionForm
        headline={interpolate(headline, { pupName: pet.name })}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={props.desktopCtaText}
        modals={mergedModals}
        ctaButtonVariant="sea2"
        centerSkip={true}
        treatsForLife={treatsForLife}
      />
    </QuizContainer>
  );
};

Extras.propTypes = {
  headline: PropTypes.string,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = () => {
  const { cart, customer } = useContext(OnboardingContext);
  const checkoutUrl = cart?.checkout_url;
  const customerEmail = customer?.email;
  useEffect(() => {
    // To maintain user continuity between domains, send the anonymousId to our Shopify Segment instance
    let anonymousId = '';
    if (window.analytics && window.analytics.user) {
      anonymousId = window.analytics.user().anonymousId();
    }
    if (checkoutUrl) {
      const url = checkoutUrl?.split('?')?.[0];
      const search = window.location?.search?.split('?')?.[1] || '';
      const initialParams = (search && `&${search}`) || '';
      const anonymousIdParam =
        (anonymousId && `&segmentAnonymousId=${anonymousId}`) || '';
      const checkoutEmailParam =
        (customerEmail &&
          `&checkout[email]=${encodeURIComponent(customerEmail)}`) ||
        '';
      // There's always at minimum 1 query parameter (key) in a Shopify checkout url
      const queryParams =
        checkoutUrl.split('?')?.[1] +
        initialParams +
        anonymousIdParam +
        checkoutEmailParam;
      window.location.href = url + `?${queryParams}`;
    }
  }, [customerEmail, checkoutUrl]);
  return <LoadingView variant="checkout" />;
};

export const components = {
  [STATE.intro]: IntroPage,
  [STATE.pupName]: PupName,
  [STATE.pupGender]: PupGender,
  [STATE.pupStatus]: PupStatus,
  [STATE.weight]: Weight,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.allergySelection]: AllergySelection,
  [STATE.userInfo]: UserInfo,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.extras]: Extras,
  [STATE.checkout]: Checkout,
};
